import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, renderList as _renderList, normalizeClass as _normalizeClass, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = ["id", "aria-labelledby"]
const _hoisted_2 = { class: "left" }
const _hoisted_3 = { key: 2 }
const _hoisted_4 = {
  key: 3,
  "aria-live": "polite",
  "aria-atomic": "true",
  class: "srOnly"
}
const _hoisted_5 = { key: 4 }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["aria-label", "onClick"]
const _hoisted_8 = { key: 5 }
const _hoisted_9 = { class: "location" }
const _hoisted_10 = ["onClick", "onKeydown"]
const _hoisted_11 = {
  key: 6,
  class: "noSuggestions"
}
const _hoisted_12 = ["onKeydown"]

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Icon } from '../../Atoms';
import { PortList, HelperTip } from '../../Molecules';
import { RecentOrSuggestedSearchEntry, RecentOrSuggestedSearchEntryGroup } from '../../types';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecentSearches',
  props: {
    title: {},
    searches: {},
    showSearches: { type: Boolean },
    showRouteMapButton: { type: Boolean },
    selectRouteMapCallback: {},
    ariaId: {},
    ariaLabeledBy: {},
    noResultsText: { default: 'No suggestions found' },
    showTextCode: { type: Boolean, default: false },
    inputRef: {},
    showHelperTip: { type: Boolean, default: false }
  },
  emits: ['setSearchValue', 'setShowSearches', 'searchClick', 'dropdownReady', 'deleteSearch'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const { t } = useI18n();

const hideDeleteButton = computed(() => props.title === t('Select a route'));

const groupedSearches = computed(() => {
  const groups: RecentOrSuggestedSearchEntryGroup[] = [];
  props.searches?.forEach((portList) => {
    if (portList.location) {
      if (!groups.find((x) => x.location === portList.location)) {
        groups.push({
          location: portList.location,
          ports: [],
        } as RecentOrSuggestedSearchEntryGroup);
      }
      const group = groups.find((x) => x.location === portList.location);
      group?.ports.push(portList);
    }
  });

  return groups;
});

const emit = __emit;

const handleTabKey = (event: KeyboardEvent) => {
  if (event.key !== 'Tab') return;

  const dropdown = (event.currentTarget as HTMLElement)?.closest('.searchAutocomplete');
  const focusableElements = dropdown?.querySelectorAll('button, [tabindex="0"]');
  const firstElement = focusableElements?.[0];
  const lastElement = focusableElements?.[focusableElements.length - 1];

  if (!dropdown || !focusableElements || focusableElements.length === 0) return;

  if (event.shiftKey && event.target === firstElement) {
    // Shift+Tab on the first item
    event.preventDefault();
    emit('setShowSearches', false);
    props.inputRef?.focus(); // Dynamic focus
  } else if (!event.shiftKey && event.target === lastElement) {
    // Tab on last item
    emit('setShowSearches', false);
  }
};

const dropdownRef = ref<HTMLElement | null>(null);

const focusDropdown = () => {
  const dropdown = dropdownRef.value;
  const firstFocusable = dropdown?.querySelector('button, [tabindex="0"]');
  (firstFocusable as HTMLElement)?.focus();
};

const closeOverlay = () => {
  emit('setShowSearches', false);
};

watch(() => props.showSearches, (newValue) => {
  if (newValue) {
    focusDropdown();
    emit('dropdownReady');
  }
});

return (_ctx: any,_cache: any) => {
  return (_ctx.showSearches)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        id: _ctx.ariaId,
        ref: "dropdown",
        "aria-labelledby": _ctx.ariaLabeledBy,
        role: "listbox",
        class: _normalizeClass(["searchAutocomplete", {emptyState: !_ctx.searches || _ctx.searches.length === 0}])
      }, [
        (_ctx.showRouteMapButton)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "routeMapButton",
              onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_ctx.selectRouteMapCallback && _ctx.selectRouteMapCallback(...args)))
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_unref(Icon), { "icon-name": "mapIconBlack" }),
                _createTextVNode(" " + _toDisplayString(_ctx.$t('Select using route map')), 1)
              ]),
              _createVNode(_unref(Icon), { "icon-name": "link" })
            ]))
          : _createCommentVNode("", true),
        (_ctx.showHelperTip)
          ? (_openBlock(), _createBlock(_unref(HelperTip), {
              key: 1,
              title: _ctx.$t('Not sure of the port'),
              text: _ctx.$t('Not sure of the port text')
            }, null, 8, ["title", "text"]))
          : _createCommentVNode("", true),
        ((_ctx.searches && _ctx.searches.length > 0) || groupedSearches.value.length > 0)
          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(_ctx.title), 1))
          : _createCommentVNode("", true),
        (_ctx.showSearches)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              ((_ctx.searches && _ctx.searches.length > 0) || groupedSearches.value.length > 0)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(_toDisplayString(_ctx.searches ? _ctx.searches.length : 0) + " suggestions available ", 1)
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(_ctx.noResultsText), 1)
                  ], 64))
            ]))
          : _createCommentVNode("", true),
        (_ctx.searches && _ctx.searches.length > 0 && groupedSearches.value.length === 0)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.searches, (search, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: index,
                  class: "portPair",
                  role: "option"
                }, [
                  _createElementVNode("button", {
                    class: _normalizeClass(["searchButton", {fullWidth: hideDeleteButton.value}]),
                    "aria-label": "Search button",
                    onClick: ($event: any) => {_ctx.$emit('setSearchValue', search);
                  _ctx.$emit('setShowSearches', false);
                  _ctx.$emit('searchClick')}
                  }, [
                    _createVNode(_unref(PortList), {
                      "icon-name": search.iconName,
                      ports: search.ports,
                      "show-text-code": _ctx.showTextCode,
                      "text-code": search.textCode,
                      class: "listItem"
                    }, null, 8, ["icon-name", "ports", "show-text-code", "text-code"])
                  ], 10, _hoisted_6),
                  (!hideDeleteButton.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "deleteButton",
                        "aria-label": `Delete search: ${search.ports[0]?.name} to ${
            search.ports[search.ports.length - 1]?.name
          }`,
                        onClick: _withModifiers(($event: any) => (_ctx.$emit('deleteSearch', search)), ["stop"])
                      }, [
                        _createVNode(_unref(Icon), {
                          "icon-name": "close",
                          sm: true
                        })
                      ], 8, _hoisted_7))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ]))
          : (groupedSearches.value && groupedSearches.value.length > 0)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedSearches.value, (group, index) => {
                  return (_openBlock(), _createElementBlock("li", { key: index }, [
                    _createElementVNode("ul", null, [
                      _createElementVNode("li", _hoisted_9, [
                        _createElementVNode("h3", null, _toDisplayString(group.location), 1)
                      ]),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.ports, (search, searchIndex) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: searchIndex,
                          class: "portPair",
                          role: "option"
                        }, [
                          _createElementVNode("button", {
                            class: "searchButton",
                            tabindex: "0",
                            onClick: ($event: any) => {
                _ctx.$emit('setSearchValue', search);
                _ctx.$emit('setShowSearches', false);
                _ctx.$emit('searchClick')
              },
                            onKeydown: [
                              _withKeys(($event: any) => {
                _ctx.$emit('setSearchValue', search);
                _ctx.$emit('setShowSearches', false);
                _ctx.$emit('searchClick')
              }, ["enter"]),
                              _withKeys(handleTabKey, ["tab"])
                            ]
                          }, [
                            _createVNode(_unref(PortList), {
                              "icon-name": search.iconName,
                              ports: search.ports,
                              class: "listItem",
                              "show-text-code": _ctx.showTextCode,
                              "text-code": search.textCode
                            }, null, 8, ["icon-name", "ports", "show-text-code", "text-code"])
                          ], 40, _hoisted_10)
                        ]))
                      }), 128))
                    ])
                  ]))
                }), 128))
              ]))
            : (!_ctx.showHelperTip)
              ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_ctx.noResultsText), 1))
              : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "srOnly closeOverlayHiddenBtn",
          "aria-label": "Close suggestions",
          onClick: closeOverlay,
          onKeydown: [
            _withKeys(_withModifiers(closeOverlay, ["prevent"]), ["enter"]),
            handleTabKey
          ]
        }, " Close overlay ", 40, _hoisted_12)
      ], 10, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})